import React, { useState, useEffect } from 'react';

import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import NouvelleConversationModal from './NouvelleConversationModal';
import { MessageSquare, Send, Paperclip, Search, User, CheckCheck, Mail, X, FileText, Download, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MessagerieDashboard() {
  const { getAuthHeader, user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchConversations();
    fetchUnreadCount();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(() => {
      if (selectedConversation) {
        fetchMessages(selectedConversation.id);
      }
      fetchUnreadCount();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/messagerie/conversations`, {
        headers: getAuthHeader()
      });
      // Trier du plus récent au plus ancien
      const sortedConversations = (response.data || []).sort((a, b) => new Date(b.date_creation || b.updated_at || b.created_at || 0) - new Date(a.date_creation || a.updated_at || a.created_at || 0));
      setConversations(sortedConversations);
    } catch {
      toast.error('Erreur lors du chargement des conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(
        `${API}/messagerie/conversations/${conversationId}/messages`,
        { headers: getAuthHeader() }
      );
      // Trier du plus récent au plus ancien
      const sortedMessages = (response.data || []).sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
      setMessages(sortedMessages);
      // Rafraîchir les conversations pour mettre à jour le compteur non lus
      fetchConversations();
      fetchUnreadCount();
    } catch {
      toast.error('Erreur lors du chargement des messages');
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/messagerie/non-lus/count`, {
        headers: getAuthHeader()
      });
      setUnreadCount(response.data.count);
    } catch {
    }
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation.id);
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Vérifier la taille totale (max 10MB par fichier)
    const oversizedFiles = files.filter(f => f.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error(`Fichier(s) trop volumineux (max 10MB) : ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }

    try {
      setUploading(true);
      const uploadedAttachments = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `${API}/messagerie/upload-piece-jointe`,
          formData,
          {
            headers: {
              ...getAuthHeader(),
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        uploadedAttachments.push(response.data);
      }

      setAttachments([...attachments, ...uploadedAttachments]);
      toast.success(`${files.length} fichier(s) ajouté(s)`);
    } catch {
      toast.error('Erreur lors de l\'upload du fichier');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (attachmentId) => {
    setAttachments(attachments.filter(a => a.id !== attachmentId));
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && attachments.length === 0) || !selectedConversation) return;

    try {
      setSending(true);
      
      // Déterminer le destinataire
      const destinataireId = selectedConversation.participant1_id === user.id
        ? selectedConversation.participant2_id
        : selectedConversation.participant1_id;
      
      const destinataireRole = selectedConversation.participant1_id === user.id
        ? selectedConversation.participant2_role
        : selectedConversation.participant1_role;

      await axios.post(
        `${API}/messagerie/conversations/${selectedConversation.id}/messages`,
        {
          destinataire_id: destinataireId,
          destinataire_role: destinataireRole,
          sujet: selectedConversation.dommage_id ? `Dommage ${selectedConversation.dommage_id}` : 'Message',
          contenu: newMessage || '(Pièces jointes)',
          type_message: 'general',
          pieces_jointes: attachments
        },
        { headers: getAuthHeader() }
      );

      setNewMessage('');
      setAttachments([]);
      fetchMessages(selectedConversation.id);
      fetchConversations();
      toast.success('Message envoyé');
    } catch {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      conv.participant1_nom.toLowerCase().includes(searchLower) ||
      conv.participant2_nom.toLowerCase().includes(searchLower)
    );
  });

  const getOtherParticipant = (conversation) => {
    if (conversation.participant1_id === user?.id) {
      return {
        nom: conversation.participant2_nom,
        role: conversation.participant2_role
      };
    }
    return {
      nom: conversation.participant1_nom,
      role: conversation.participant1_role
    };
  };

  const handleConversationCreated = (conversation) => {
    fetchConversations();
    setSelectedConversation(conversation);
    fetchMessages(conversation.id);
  };

  return (
    <>
      <NouvelleConversationModal
        isOpen={showNewConversationModal}
        onClose={() => setShowNewConversationModal(false)}
        onConversationCreated={handleConversationCreated}
      />

      <div className="space-y-4">
        {/* Hero Header — Ultra Pro */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-blue-900 to-cyan-900 p-5 lg:p-6 text-white shadow-2xl">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(56,189,248,0.4),_transparent_55%)] pointer-events-none" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_rgba(59,130,246,0.25),_transparent_45%)] pointer-events-none" />
          <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 via-cyan-500 to-sky-400 flex items-center justify-center shadow-lg shadow-cyan-500/40 flex-shrink-0">
                <MessageSquare className="h-6 w-6 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-200 border border-cyan-400/30 backdrop-blur-sm font-semibold text-xs flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                    Sync auto · 30s
                  </span>
                  {unreadCount > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-rose-500/30 text-rose-100 border border-rose-400/40 backdrop-blur-sm font-bold text-xs">
                      {unreadCount} non lu{unreadCount > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <h2 className="text-xl lg:text-2xl font-bold">Messagerie interne</h2>
                <p className="text-cyan-100 mt-0.5 text-sm">
                  {conversations.length} conversation{conversations.length > 1 ? 's' : ''} • réponses en temps réel
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowNewConversationModal(true)}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white border-0 shadow-lg shadow-cyan-500/30"
                data-testid="messagerie-new-conversation"
              >
                <Plus className="h-4 w-4 mr-1" />
                Nouvelle conversation
              </Button>
            </div>
          </div>
        </div>

        <div className="h-[calc(100vh-16rem)] flex gap-4">
      {/* Liste des conversations */}
      <div className="w-1/3 flex flex-col bg-white rounded-lg border border-slate-200">
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Conversations
              {unreadCount > 0 && (
                <Badge className="bg-red-600 text-white font-semibold">{unreadCount}</Badge>
              )}
            </h2>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Mail className="h-12 w-12 mx-auto mb-2 text-slate-300" />
              <p>Aucune conversation</p>
            </div>
          ) : (
            filteredConversations.map((conv) => {
              const other = getOtherParticipant(conv);
              const isSelected = selectedConversation?.id === conv.id;
              
              return (
                <div
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv)}
                  className={`p-4 border-b border-slate-100 cursor-pointer transition-colors ${
                    isSelected ? 'bg-blue-50 border-l-4 border-l-blue-600' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{other.nom}</p>
                        <Badge className="bg-slate-600 text-white text-xs font-semibold">
                          {other.role}
                        </Badge>
                      </div>
                    </div>
                    {conv.messages_non_lus > 0 && (
                      <Badge className="bg-red-600 text-white font-semibold">
                        {conv.messages_non_lus}
                      </Badge>
                    )}
                  </div>
                  {conv.dernier_message && (
                    <p className="text-sm text-slate-600 truncate mt-2">
                      {conv.dernier_message}
                    </p>
                  )}
                  {conv.dernier_message_at && (
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(conv.dernier_message_at).toLocaleString('fr-FR')}
                    </p>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Zone de messages */}
      <div className="flex-1 flex flex-col bg-white rounded-lg border border-slate-200">
        {selectedConversation ? (
          <>
            {/* Header */}
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">
                      {getOtherParticipant(selectedConversation).nom}
                    </h3>
                    <p className="text-sm text-slate-600">
                      {getOtherParticipant(selectedConversation).role}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => {
                const isMe = message.expediteur_id === user?.id;
                
                return (
                  <div
                    key={message.id}
                    className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] ${isMe ? 'order-2' : 'order-1'}`}>
                      <div
                        className={`rounded-lg p-4 ${
                          isMe
                            ? 'bg-blue-600 text-white'
                            : 'bg-slate-100 text-slate-900'
                        }`}
                      >
                        <p className="text-sm font-semibold mb-1">
                          {isMe ? 'Vous' : `${message.expediteur_prenom} ${message.expediteur_nom}`}
                        </p>
                        <p className="whitespace-pre-wrap">{message.contenu}</p>
                        {message.pieces_jointes?.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {message.pieces_jointes.map((pj) => (
                              <div
                                key={pj.id}
                                className={`flex items-center justify-between gap-3 p-2 rounded ${
                                  isMe ? 'bg-blue-700' : 'bg-white border border-slate-200'
                                }`}
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <div className={`p-2 rounded ${isMe ? 'bg-blue-600' : 'bg-blue-50'}`}>
                                    <FileText className={`h-4 w-4 ${isMe ? 'text-white' : 'text-blue-600'}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium truncate ${
                                      isMe ? 'text-white' : 'text-slate-900'
                                    }`}>
                                      {pj.nom_fichier}
                                    </p>
                                    <p className={`text-xs ${
                                      isMe ? 'text-blue-100' : 'text-slate-500'
                                    }`}>
                                      {(pj.taille / 1024).toFixed(1)} KB
                                    </p>
                                  </div>
                                </div>
                                <a
                                  href={`${BACKEND_URL}${pj.url}`}
                                  download={pj.nom_fichier}
                                  className={`p-2 rounded transition-colors ${
                                    isMe 
                                      ? 'hover:bg-blue-600 text-white' 
                                      : 'hover:bg-slate-100 text-blue-600'
                                  }`}
                                  title="Télécharger"
                                >
                                  <Download className="h-4 w-4" />
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <p className={`text-xs mt-1 ${isMe ? 'text-right' : 'text-left'} text-slate-400`}>
                        {new Date(message.created_at).toLocaleString('fr-FR')}
                        {isMe && message.statut === 'lu' && (
                          <CheckCheck className="inline ml-1 h-3 w-3 text-blue-600" />
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Input zone */}
            <div className="p-4 border-t border-slate-200">
              {/* Pièces jointes en cours */}
              {attachments.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm"
                    >
                      <Paperclip className="h-4 w-4 text-blue-600" />
                      <span className="text-blue-900 truncate max-w-[200px]">
                        {attachment.nom_fichier}
                      </span>
                      <span className="text-xs text-blue-600">
                        ({(attachment.taille / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        onClick={() => removeAttachment(attachment.id)}
                        className="text-blue-600 hover:text-red-600 transition-colors ml-1"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <div className="flex flex-col flex-1 gap-2">
                  <Textarea
                    placeholder="Écrivez votre message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    rows={3}
                    className="flex-1"
                  />
                  <div className="flex items-center gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        multiple
                        onChange={handleFileUpload}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploading}
                        asChild
                      >
                        <span>
                          <Paperclip className="h-4 w-4 mr-2" />
                          {uploading ? 'Upload...' : 'Joindre'}
                        </span>
                      </Button>
                    </label>
                    <p className="text-xs text-slate-500">
                      Entrée pour envoyer, Shift+Entrée pour nouvelle ligne
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleSendMessage}
                  disabled={(!newMessage.trim() && attachments.length === 0) || sending}
                  className="bg-blue-600 hover:bg-blue-700 self-start"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <div className="text-center">
              <MessageSquare className="h-16 w-16 mx-auto mb-4" />
              <p className="text-lg">Sélectionnez une conversation</p>
              <p className="text-sm">pour voir les messages</p>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
    </>
  );
}
